import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Search, X, Check } from 'lucide-react';
import { quantUniversePublic } from '../lib/api';

/**
 * Hook condiviso: carica universe lazy una volta per sessione (cache in modulo).
 * Ritorna {items, classLabels, loading, error}.
 */
let _cache = null;
let _inflight = null;

export function useUniverse() {
  const [state, setState] = useState(() => _cache || { items: [], classLabels: {}, loading: !_cache });

  useEffect(() => {
    if (_cache) return;
    if (!_inflight) {
      _inflight = quantUniversePublic()
        .then((res) => {
          _cache = {
            items: res.data.items || [],
            classLabels: res.data.class_labels || {},
            loading: false,
          };
          return _cache;
        })
        .catch((err) => {
          _cache = { items: [], classLabels: {}, loading: false, error: err };
          return _cache;
        })
        .finally(() => { _inflight = null; });
    }
    _inflight.then(setState);
  }, []);

  return state;
}

/**
 * Picker generico: multi o single. Mostra nome + ticker, raggruppa per classe.
 * Search case-insensitive su name OR symbol.
 *
 * Props:
 *  - value: string (single) | string[] (multi)
 *  - onChange: (next) => void
 *  - multi: boolean (default false)
 *  - placeholder: string
 *  - disabled: boolean
 *  - autoFocus: boolean
 *  - dataTestId: string
 */
const AssetPickerBase = ({
  value,
  onChange,
  multi = false,
  placeholder = 'Cerca per nome o ticker (es. "s&p", "MSCI World", "SPY")',
  disabled = false,
  autoFocus = false,
  dataTestId,
}) => {
  const { items, classLabels, loading } = useUniverse();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selectedSet = useMemo(() => {
    if (multi) return new Set((value || []).map((s) => s.toUpperCase()));
    return new Set(value ? [value.toUpperCase()] : []);
  }, [value, multi]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (a) =>
        a.symbol.toLowerCase().includes(q) ||
        a.name.toLowerCase().includes(q)
    );
  }, [items, query]);

  // Raggruppo per classe mantenendo l'ordine di CLASS_LABELS
  const groups = useMemo(() => {
    const byClass = {};
    for (const a of filtered) {
      const k = a.asset_class || 'other';
      if (!byClass[k]) byClass[k] = [];
      byClass[k].push(a);
    }
    const order = Object.keys(classLabels);
    return Object.keys(byClass)
      .sort((x, y) => {
        const ix = order.indexOf(x);
        const iy = order.indexOf(y);
        if (ix !== -1 && iy !== -1) return ix - iy;
        if (ix !== -1) return -1;
        if (iy !== -1) return 1;
        return x.localeCompare(y);
      })
      .map((k) => ({ key: k, label: classLabels[k] || k, items: byClass[k] }));
  }, [filtered, classLabels]);

  const toggle = useCallback(
    (symbol) => {
      const sym = symbol.toUpperCase();
      if (multi) {
        const arr = value || [];
        const has = arr.includes(sym);
        onChange(has ? arr.filter((s) => s !== sym) : [...arr, sym]);
      } else {
        onChange(sym);
        setOpen(false);
        setQuery('');
      }
    },
    [multi, value, onChange]
  );

  const removeChip = useCallback(
    (symbol) => onChange((value || []).filter((s) => s !== symbol)),
    [value, onChange]
  );

  // Helper: nome esteso per un ticker (per single-mode display nel campo)
  const labelFor = useCallback(
    (sym) => {
      const a = items.find((x) => x.symbol === sym);
      return a ? `${a.name}` : sym;
    },
    [items]
  );

  return (
    <div className="space-y-2" ref={wrapperRef} data-testid={dataTestId}>
      {/* Chip per multi-select */}
      {multi && (value?.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((sym) => (
            <Badge key={sym} variant="secondary" className="flex items-center gap-1 pr-1">
              <span className="font-mono text-[10px]">{sym}</span>
              <span className="text-[10px] text-muted-foreground hidden sm:inline">
                {labelFor(sym).slice(0, 28)}
              </span>
              <button
                type="button"
                onClick={() => removeChip(sym)}
                className="hover:bg-destructive/20 rounded p-0.5"
                aria-label={`Rimuovi ${sym}`}
                disabled={disabled}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          type="text"
          value={!multi && value && !open ? `${value} · ${labelFor(value)}` : query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={loading ? 'Caricamento universe…' : placeholder}
          disabled={disabled || loading}
          autoFocus={autoFocus}
          className="pl-9"
        />
      </div>

      {/* Dropdown */}
      {open && !disabled && (
        <div className="relative">
          <div className="absolute left-0 right-0 z-50 max-h-72 overflow-y-auto rounded-md border bg-popover shadow-lg">
            {groups.length === 0 ? (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                {loading ? 'Caricamento…' : query ? `Nessun risultato per "${query}"` : 'Nessun asset disponibile'}
              </div>
            ) : (
              groups.map((g) => (
                <div key={g.key}>
                  <div className="px-3 py-1.5 text-[10px] uppercase font-bold tracking-wider text-muted-foreground bg-muted/50 sticky top-0">
                    {g.label}
                    <span className="ml-1.5 text-muted-foreground/60 font-normal">({g.items.length})</span>
                  </div>
                  {g.items.map((a) => {
                    const isSelected = selectedSet.has(a.symbol.toUpperCase());
                    return (
                      <button
                        key={a.symbol}
                        type="button"
                        onClick={() => toggle(a.symbol)}
                        className={`flex items-center w-full px-3 py-2 text-left text-sm hover:bg-accent transition-colors ${
                          isSelected ? 'bg-primary/10' : ''
                        }`}
                      >
                        <div className="w-5 mr-2 flex-shrink-0">
                          {isSelected && <Check className="w-4 h-4 text-primary" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{a.name}</div>
                          {a.notes && (
                            <div className="text-xs text-muted-foreground truncate">{a.notes}</div>
                          )}
                        </div>
                        <span className="font-mono text-xs text-muted-foreground ml-2 shrink-0">
                          {a.symbol}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const AssetMultiPicker = (props) => <AssetPickerBase {...props} multi />;
export const AssetPicker = (props) => <AssetPickerBase {...props} multi={false} />;
export default AssetMultiPicker;
